import { ProductDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';

export const toppingIngredientCategoryId = Commerce.IngredientCategory.INGREDIENT;
export const baseDoughCategoryId = Commerce.BaseIngredientCategory.BASE_DOUGH;

export const pizzaProductCategoryIds: number[] = [
  Commerce.ProductCategory.PIZZA,
  Commerce.ProductCategory.KOTZONE,
  Commerce.ProductCategory.MONSTER,
  Commerce.ProductCategory.LANKKU,
  Commerce.ProductCategory.RULLA,
  Commerce.ProductCategory.ETUPIZZA,
  Commerce.ProductCategory.HAPANJUURI,
  Commerce.ProductCategory.MINIMONSTER,
];

export const pizzaIngredientCategoryIds: number[] = [
  Commerce.IngredientCategory.INGREDIENT,
  Commerce.IngredientCategory.CHEESE,
  Commerce.IngredientCategory.DOUGH,
  Commerce.IngredientCategory.BASE_SAUCE,
  Commerce.IngredientCategory.SAUCE,
  Commerce.IngredientCategory.SPICE,
  Commerce.IngredientCategory.DIP
];

/**
 * Products whose S4D options are per dough (`${productSizeId}_${doughId}`). Everything else is sold
 * in S4D as one dough-less option per size (`${productSizeId}_0`) - a rulla, a salad or a drink has
 * no dough to choose. Only consulted when S4D has no option for the size at all; when it has one,
 * its shape tells which of the two is expected.
 */
export const doughOptionProductCategoryIds = [
  Commerce.ProductCategory.PIZZA,
  Commerce.ProductCategory.KOTZONE,
  Commerce.ProductCategory.MONSTER,
  Commerce.ProductCategory.LANKKU,
  Commerce.ProductCategory.ETUPIZZA,
  Commerce.ProductCategory.HAPANJUURI,
  Commerce.ProductCategory.MINIMONSTER,
];

/**
 * Products whose ingredients reach S4D at all. The order injector maps added and removed ingredients
 * only for these categories, so the recipe of anything else (drinks, dips, ice cream, snacks,
 * alcohol) is never compared against S4D. Mirrors the injector's
 * `ProductCategoriesWithModifiableIngredients`.
 */
export const productCategoriesWithS4DRecipes = [
  Commerce.ProductCategory.PIZZA,
  Commerce.ProductCategory.KOTZONE,
  Commerce.ProductCategory.MONSTER,
  Commerce.ProductCategory.LANKKU,
  Commerce.ProductCategory.DIPATTAVAT,
  Commerce.ProductCategory.RULLA,
  Commerce.ProductCategory.ETUPIZZA,
  Commerce.ProductCategory.HAPANJUURI,
  Commerce.ProductCategory.MINIMONSTER,
  Commerce.ProductCategory.SALAD,
];

/**
 * Ingredient categories that are never part of an S4D recipe, because the S4D order injector turns
 * them into separate S4D products before the order is sent (dips), or maps them to the product's
 * option rather than a topping (doughs).
 */
export const ingredientCategoriesOutsideS4DRecipes = [
  Commerce.IngredientCategory.DIP,
  Commerce.IngredientCategory.DOUGH,
];

/**
 * Products whose top sauce (`IngredientCategory.SAUCE`) is not part of the S4D recipe: the order
 * injector sends it as an added topping instead. Mirrors the injector's `TopSauceProductCategoryIds`
 * app setting in production, not its unit tests, which also include rullas.
 */
export const topSauceProductCategoryIds = [
  Commerce.ProductCategory.KOTZONE,
  Commerce.ProductCategory.LANKKU,
  Commerce.ProductCategory.SALAD,
];

export const pizzaBaseIngredientCategoryIds = [
  Commerce.IngredientCategory.CHEESE,
  Commerce.IngredientCategory.DOUGH,
  Commerce.IngredientCategory.BASE_SAUCE,
];

export const doughProductSizeIds = [
  Commerce.ProductSize.PIZZA_LARGE,
  Commerce.ProductSize.PIZZA_MEDIUM,
  Commerce.ProductSize.KOTZONE,
  Commerce.ProductSize.MONSTER_MEDIUM,
  Commerce.ProductSize.MINIMONSTER,
  Commerce.ProductSize.MONSTER_LARGE,
  Commerce.ProductSize.LANKKU,
  Commerce.ProductSize.RULLA
];

export const ovenProductSizeIds = [
  Commerce.ProductSize.PIZZA_LARGE,
  Commerce.ProductSize.PIZZA_MEDIUM,
  Commerce.ProductSize.KOTZONE,
  Commerce.ProductSize.MONSTER_MEDIUM,
  Commerce.ProductSize.MINIMONSTER,
  Commerce.ProductSize.MONSTER_LARGE,
  Commerce.ProductSize.LANKKU,
  Commerce.ProductSize.RULLA,
  Commerce.ProductSize.DIPATTAVAT
];

export const ovenProductCategoryIds = [
  Commerce.ProductCategory.PIZZA,
  Commerce.ProductCategory.RULLA,
  Commerce.ProductCategory.MONSTER,
  Commerce.ProductCategory.KOTZONE,
  Commerce.ProductCategory.LANKKU,
  Commerce.ProductCategory.MINIMONSTER,
];

export const sliceProductSizes = [
  { title: 'Pizzat: Large', value: Commerce.ProductSize.PIZZA_LARGE.toString() },
  { title: 'Pizzat: Medium', value: Commerce.ProductSize.PIZZA_MEDIUM.toString() },
  { title: 'Kotzonet: Kotzone', value: Commerce.ProductSize.KOTZONE.toString() },
  { title: 'Monsterit: Monster', value: Commerce.ProductSize.MONSTER_MEDIUM.toString() },
  { title: 'Luksuslankut: Lankkupizza', value: Commerce.ProductSize.LANKKU.toString() },
  { title: 'Monsterit: Monster Large', value: Commerce.ProductSize.MONSTER_LARGE.toString() },
  { title: 'Rullat: Rulla', value: Commerce.ProductSize.RULLA.toString() },
  { title: 'Salaatit: Salaatti', value: Commerce.ProductSize.SALAD_MEDIUM.toString() },
];

/** The productSizeIds behind sliceProductSizes, which stores them as strings for select inputs. */
export const sliceProductSizeIds: number[] = sliceProductSizes.map((size) => Number(size.value));

/** True when a product size is one of the slice sizes. */
export const isSliceProductSize = <T extends { productSizeId: number }>(size: T): boolean =>
  sliceProductSizeIds.includes(size.productSizeId);

/**
 * The product categories reachable through the slice sizes. A product's selectable sizes
 * are derived from its category rather than stored per product, so a product only belongs
 * in a slice-size picker when its category owns at least one slice size.
 */
export const getSliceProductCategoryIds = <T extends { productSizeId: number; productCategoryId: number }>(
  productSizes: T[]
): Set<number> => new Set(productSizes.filter(isSliceProductSize).map((size) => size.productCategoryId));

/**
 * Sold in the webshop: active, not archived and within its start and end dates, as products-api's
 * `CommerceProductService` checks.
 */
export const isLive = (product: ProductDto | undefined): product is ProductDto => {
  if (!product || !product.active || product.isArchived) return false;

  const now = Date.now();
  return (
    (!product.startDate || new Date(product.startDate).getTime() <= now) &&
    (!product.endDate || new Date(product.endDate).getTime() >= now)
  );
};
